import { useEffect } from 'react';

export const AUTH_SESSION_EXPIRED_EVENT = 'auth:session-expired';
export const INACTIVE_ORGANIZATION_ERROR_MESSAGE = 'Organization is inactive. Contact System Administrator.';

export function isInactiveOrganizationErrorMessage(message) {
  const normalized = String(message || '').trim().toLowerCase();
  return normalized.includes('organization') && normalized.includes('inactive');
}

/**
 * Normalizes forbidden inactive-organization errors to the shared contract message.
 * For non-403 responses, returns the provided message/fallback unchanged.
 */
export function normalizeInactiveOrganizationError(status, message, fallbackMessage = '') {
  if (status !== 403) {
    return message || fallbackMessage || '';
  }

  if (isInactiveOrganizationErrorMessage(message)) {
    return INACTIVE_ORGANIZATION_ERROR_MESSAGE;
  }

  return message || fallbackMessage || '';
}

export function emitSessionExpiredEvent() {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') {
    return;
  }

  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
}

export function useAutoDismissAlerts({ error, success, setError, setSuccess, delayMs = 5000 }) {
  useEffect(() => {
    if (!error && !success) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setError('');
      setSuccess('');
    }, delayMs);

    return () => clearTimeout(timer);
  }, [delayMs, error, setError, setSuccess, success]);
}

export async function readAdminApiErrorMessage(res, fallbackMessages = {}) {
  let rawText = '';
  let parsedBody = null;

  if (res.status === 401) {
    emitSessionExpiredEvent();
  }

  try {
    rawText = await res.text();
    if (rawText) {
      try {
        parsedBody = JSON.parse(rawText);
      } catch {
        parsedBody = rawText;
      }
    }
  } catch {
    // Ignore response parsing failures and fall back to status-based messages.
  }

  const structuredErrors = Array.isArray(parsedBody?.errors)
    ? parsedBody.errors
        .map((entry) => {
          if (typeof entry === 'string') {
            return entry;
          }

          return entry?.errorMessage || entry?.message;
        })
        .filter(Boolean)
    : [];

  if (structuredErrors.length > 0) {
    return structuredErrors.join(' ');
  }

  const validationErrors = parsedBody?.errors && typeof parsedBody.errors === 'object' && !Array.isArray(parsedBody.errors)
    ? Object.values(parsedBody.errors).flat().filter(Boolean)
    : [];

  if (validationErrors.length > 0) {
    return validationErrors.join(' ');
  }

  const explicitMessage =
    (typeof parsedBody?.message === 'string' && parsedBody.message) ||
    (typeof parsedBody?.title === 'string' && parsedBody.title) ||
    (typeof parsedBody === 'string' && parsedBody) ||
    rawText;

  if (explicitMessage) {
    return normalizeInactiveOrganizationError(res.status, explicitMessage, fallbackMessages[res.status]);
  }

  return normalizeInactiveOrganizationError(
    res.status,
    '',
    fallbackMessages[res.status] || fallbackMessages.default || 'An unexpected error occurred.'
  );
}
